import { useState } from "react";
import { useNossaHistoria, NossaHistoriaContent } from "@/hooks/useNossaHistoria";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Save } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export function ContentManager() {
  const { content, contentLoading, updateContent } = useNossaHistoria();
  const [formData, setFormData] = useState<Record<string, any>>({});

  const getContentBySection = (section: string) => {
    return content?.filter(c => c.section === section) || [];
  };

  const handleUpdateContent = (contentItem: NossaHistoriaContent, newValue: string) => {
    const updatedValue = { ...contentItem.content_value, text: newValue };
    updateContent({ 
      id: contentItem.id, 
      updates: { content_value: updatedValue } 
    });
  };

  if (contentLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-32 w-full" />
        ))}
      </div>
    );
  }

  return (
    <Tabs defaultValue="hero" className="w-full">
      <TabsList>
        <TabsTrigger value="hero">Hero</TabsTrigger>
        <TabsTrigger value="story">História</TabsTrigger>
        <TabsTrigger value="principle">Princípios</TabsTrigger>
      </TabsList>

      <TabsContent value="hero" className="space-y-4 mt-4">
        {getContentBySection('hero').map((item) => (
          <Card key={item.id}>
            <CardHeader>
              <CardTitle className="text-sm">{item.content_key}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                value={formData[item.id] || item.content_value.text || ''}
                onChange={(e) => setFormData({ ...formData, [item.id]: e.target.value })}
                rows={3}
              />
              <Button 
                size="sm"
                onClick={() => handleUpdateContent(item, formData[item.id] || item.content_value.text)}
              >
                <Save className="w-4 h-4 mr-2" />
                Salvar
              </Button>
            </CardContent>
          </Card>
        ))}
      </TabsContent>

      <TabsContent value="story" className="space-y-4 mt-4">
        {getContentBySection('story').map((item) => (
          <Card key={item.id}>
            <CardHeader>
              <CardTitle className="text-sm capitalize">{item.content_key.replace('_', ' ')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {item.content_key === 'crn' ? (
                <div className="space-y-2">
                  <Label>CRN</Label>
                  <Input
                    value={formData[item.id] || item.content_value.text || ''}
                    onChange={(e) => setFormData({ ...formData, [item.id]: e.target.value })}
                    placeholder="Ex: 3/63492"
                  />
                </div>
              ) : (
                <Textarea
                  value={formData[item.id] || item.content_value.text || ''}
                  onChange={(e) => setFormData({ ...formData, [item.id]: e.target.value })}
                  rows={item.content_key.includes('paragraph') ? 5 : 2}
                />
              )}
              <Button 
                size="sm"
                onClick={() => handleUpdateContent(item, formData[item.id] || item.content_value.text)}
              >
                <Save className="w-4 h-4 mr-2" />
                Salvar
              </Button>
            </CardContent>
          </Card>
        ))}
      </TabsContent>

      <TabsContent value="principle" className="space-y-4 mt-4">
        <div className="text-sm text-muted-foreground mb-4">
          Os princípios são exibidos como cards na página. Edite o título e descrição de cada um.
        </div>
        {getContentBySection('principle').map((item) => (
          <Card key={item.id}>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                {item.content_value.icon && (
                  <span className="text-xs bg-muted px-2 py-1 rounded">{item.content_value.icon}</span>
                )}
                {item.content_value.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label>Título</Label>
                <Input
                  value={formData[`${item.id}_title`] || item.content_value.title || ''}
                  onChange={(e) => setFormData({ ...formData, [`${item.id}_title`]: e.target.value })}
                />
              </div>
              <div>
                <Label>Descrição</Label>
                <Textarea
                  value={formData[`${item.id}_desc`] || item.content_value.description || ''}
                  onChange={(e) => setFormData({ ...formData, [`${item.id}_desc`]: e.target.value })}
                  rows={4}
                />
              </div>
              <Button 
                size="sm"
                onClick={() => {
                  const updatedValue = {
                    ...item.content_value,
                    title: formData[`${item.id}_title`] || item.content_value.title,
                    description: formData[`${item.id}_desc`] || item.content_value.description
                  };
                  updateContent({ 
                    id: item.id, 
                    updates: { content_value: updatedValue } 
                  });
                }}
              >
                <Save className="w-4 h-4 mr-2" />
                Salvar
              </Button>
            </CardContent>
          </Card>
        ))}
      </TabsContent>
    </Tabs>
  );
}